import { useEffect, useState } from 'react';
import { cva } from 'class-variance-authority';
import styles from './Icon.module.css';
import {
  foregroundColorVariants,
  type ForegroundVariants,
} from '../../utils/foregroundColour/foregroundColor';

import {
  backgroundColorVariants,
  type BackgroundVariants,
} from '../../utils/backgroundColor/backgroundColor';
import {
  generateResponsiveClasses,
  getBaseValue,
  type ResponsiveValue,
} from '../../utils/breakpoint/responsiveSSR';
import { altoConsole } from '../../utils/console/console';

const getResponsiveClasses = generateResponsiveClasses(styles);

const iconWrapper = cva(styles['icon-wrapper'], {
  variants: {
    iconSize: {
      '0.5x': styles['icon-wrapper--size-0_5x'],
      '0.75x': styles['icon-wrapper--size-0_75x'],
      '1x': styles['icon-wrapper--size-1x'],
      '1.25x': styles['icon-wrapper--size-1_25x'],
      '1.5x': styles['icon-wrapper--size-1_5x'],
      '2x': styles['icon-wrapper--size-2x'],
      '2.5x': styles['icon-wrapper--size-2_5x'],
      '3x': styles['icon-wrapper--size-3x'],
      '4x': styles['icon-wrapper--size-4x'],
      '5x': styles['icon-wrapper--size-5x'],
      '6x': styles['icon-wrapper--size-6x'],
      '7x': styles['icon-wrapper--size-7x'],
      '8x': styles['icon-wrapper--size-8x'],
      '9x': styles['icon-wrapper--size-9x'],
      '10x': styles['icon-wrapper--size-10x'],
      default: styles['icon-wrapper--size-1x'],
      lg: styles['icon-wrapper--size-1_25x'],
      sm: styles['icon-wrapper--size-1x'],
      xs: styles['icon-wrapper--size-1x'],
      '2xs': styles['icon-wrapper--size-0_75x'],
      xl: styles['icon-wrapper--size-2x'],
      '2xl': styles['icon-wrapper--size-3x'],
    },
    padding: {
      none: '',
      fixedWidth: '',
      square: '',
      roomy: '',
    },
  },
  compoundVariants: [
    // 0.5x
    {
      iconSize: '0.5x',
      padding: 'none',
      class: styles['icon-wrapper--0_5--none'],
    },
    {
      iconSize: '0.5x',
      padding: 'fixedWidth',
      class: styles['icon-wrapper--0_5--fixedwidth'],
    },
    {
      iconSize: '0.5x',
      padding: 'square',
      class: styles['icon-wrapper--0_5--square'],
    },
    {
      iconSize: '0.5x',
      padding: 'roomy',
      class: styles['icon-wrapper--0_5--roomy'],
    },
    // 0.75x
    {
      iconSize: '0.75x',
      padding: 'none',
      class: styles['icon-wrapper--0_75--none'],
    },
    {
      iconSize: '0.75x',
      padding: 'fixedWidth',
      class: styles['icon-wrapper--0_75--fixedwidth'],
    },
    {
      iconSize: '0.75x',
      padding: 'square',
      class: styles['icon-wrapper--0_75--square'],
    },
    {
      iconSize: '0.75x',
      padding: 'roomy',
      class: styles['icon-wrapper--0_75--roomy'],
    },
    // 1x
    {
      iconSize: '1x',
      padding: 'none',
      class: styles['icon-wrapper--1--none'],
    },
    {
      iconSize: '1x',
      padding: 'fixedWidth',
      class: styles['icon-wrapper--1--fixedwidth'],
    },
    {
      iconSize: '1x',
      padding: 'square',
      class: styles['icon-wrapper--1--square'],
    },
    {
      iconSize: '1x',
      padding: 'roomy',
      class: styles['icon-wrapper--1--roomy'],
    },
    // 1.25x
    {
      iconSize: '1.25x',
      padding: 'none',
      class: styles['icon-wrapper--1_25--none'],
    },
    {
      iconSize: '1.25x',
      padding: 'fixedWidth',
      class: styles['icon-wrapper--1_25--fixedwidth'],
    },
    {
      iconSize: '1.25x',
      padding: 'square',
      class: styles['icon-wrapper--1_25--square'],
    },
    {
      iconSize: '1.25x',
      padding: 'roomy',
      class: styles['icon-wrapper--1_25--roomy'],
    },
    // 1.5x
    {
      iconSize: '1.5x',
      padding: 'none',
      class: styles['icon-wrapper--1_5--none'],
    },
    {
      iconSize: '1.5x',
      padding: 'fixedWidth',
      class: styles['icon-wrapper--1_5--fixedwidth'],
    },
    {
      iconSize: '1.5x',
      padding: 'square',
      class: styles['icon-wrapper--1_5--square'],
    },
    {
      iconSize: '1.5x',
      padding: 'roomy',
      class: styles['icon-wrapper--1_5--roomy'],
    },
    // 2x
    {
      iconSize: '2x',
      padding: 'none',
      class: styles['icon-wrapper--2--none'],
    },
    {
      iconSize: '2x',
      padding: 'fixedWidth',
      class: styles['icon-wrapper--2--fixedwidth'],
    },
    {
      iconSize: '2x',
      padding: 'square',
      class: styles['icon-wrapper--2--square'],
    },
    {
      iconSize: '2x',
      padding: 'roomy',
      class: styles['icon-wrapper--2--roomy'],
    },
    // 2.5x
    {
      iconSize: '2.5x',
      padding: 'none',
      class: styles['icon-wrapper--2_5--none'],
    },
    {
      iconSize: '2.5x',
      padding: 'fixedWidth',
      class: styles['icon-wrapper--2_5--fixedwidth'],
    },
    {
      iconSize: '2.5x',
      padding: 'square',
      class: styles['icon-wrapper--2_5--square'],
    },
    {
      iconSize: '2.5x',
      padding: 'roomy',
      class: styles['icon-wrapper--2_5--roomy'],
    },
    // 3x
    {
      iconSize: '3x',
      padding: 'none',
      class: styles['icon-wrapper--3--none'],
    },
    {
      iconSize: '3x',
      padding: 'fixedWidth',
      class: styles['icon-wrapper--3--fixedwidth'],
    },
    {
      iconSize: '3x',
      padding: 'square',
      class: styles['icon-wrapper--3--square'],
    },
    {
      iconSize: '3x',
      padding: 'roomy',
      class: styles['icon-wrapper--3--roomy'],
    },
    // 4x
    {
      iconSize: '4x',
      padding: 'none',
      class: styles['icon-wrapper--4--none'],
    },
    {
      iconSize: '4x',
      padding: 'fixedWidth',
      class: styles['icon-wrapper--4--fixedwidth'],
    },
    {
      iconSize: '4x',
      padding: 'square',
      class: styles['icon-wrapper--4--square'],
    },
    {
      iconSize: '4x',
      padding: 'roomy',
      class: styles['icon-wrapper--4--roomy'],
    },
    // 5x
    {
      iconSize: '5x',
      padding: 'none',
      class: styles['icon-wrapper--5--none'],
    },
    {
      iconSize: '5x',
      padding: 'fixedWidth',
      class: styles['icon-wrapper--5--fixedwidth'],
    },
    {
      iconSize: '5x',
      padding: 'square',
      class: styles['icon-wrapper--5--square'],
    },
    {
      iconSize: '5x',
      padding: 'roomy',
      class: styles['icon-wrapper--5--roomy'],
    },
    // 6x
    {
      iconSize: '6x',
      padding: 'none',
      class: styles['icon-wrapper--6--none'],
    },
    {
      iconSize: '6x',
      padding: 'fixedWidth',
      class: styles['icon-wrapper--6--fixedwidth'],
    },
    {
      iconSize: '6x',
      padding: 'square',
      class: styles['icon-wrapper--6--square'],
    },
    {
      iconSize: '6x',
      padding: 'roomy',
      class: styles['icon-wrapper--6--roomy'],
    },
    // 7x
    {
      iconSize: '7x',
      padding: 'none',
      class: styles['icon-wrapper--7--none'],
    },
    {
      iconSize: '7x',
      padding: 'fixedWidth',
      class: styles['icon-wrapper--7--fixedwidth'],
    },
    {
      iconSize: '7x',
      padding: 'square',
      class: styles['icon-wrapper--7--square'],
    },
    {
      iconSize: '7x',
      padding: 'roomy',
      class: styles['icon-wrapper--7--roomy'],
    },
    // 8x
    {
      iconSize: '8x',
      padding: 'none',
      class: styles['icon-wrapper--8--none'],
    },
    {
      iconSize: '8x',
      padding: 'fixedWidth',
      class: styles['icon-wrapper--8--fixedwidth'],
    },
    {
      iconSize: '8x',
      padding: 'square',
      class: styles['icon-wrapper--8--square'],
    },
    {
      iconSize: '8x',
      padding: 'roomy',
      class: styles['icon-wrapper--8--roomy'],
    },
    // 9x
    {
      iconSize: '9x',
      padding: 'none',
      class: styles['icon-wrapper--9--none'],
    },
    {
      iconSize: '9x',
      padding: 'fixedWidth',
      class: styles['icon-wrapper--9--fixedwidth'],
    },
    {
      iconSize: '9x',
      padding: 'square',
      class: styles['icon-wrapper--9--square'],
    },
    {
      iconSize: '9x',
      padding: 'roomy',
      class: styles['icon-wrapper--9--roomy'],
    },
    // 10x
    {
      iconSize: '10x',
      padding: 'none',
      class: styles['icon-wrapper--10--none'],
    },
    {
      iconSize: '10x',
      padding: 'fixedWidth',
      class: styles['icon-wrapper--10--fixedwidth'],
    },
    {
      iconSize: '10x',
      padding: 'square',
      class: styles['icon-wrapper--10--square'],
    },
    {
      iconSize: '10x',
      padding: 'roomy',
      class: styles['icon-wrapper--10--roomy'],
    },
    // Aliases map to their base sizes
    {
      iconSize: 'default',
      padding: 'none',
      class: styles['icon-wrapper--1--none'],
    },
    {
      iconSize: 'default',
      padding: 'fixedWidth',
      class: styles['icon-wrapper--1--fixedwidth'],
    },
    {
      iconSize: 'default',
      padding: 'square',
      class: styles['icon-wrapper--1--square'],
    },
    {
      iconSize: 'default',
      padding: 'roomy',
      class: styles['icon-wrapper--1--roomy'],
    },
    {
      iconSize: 'lg',
      padding: 'none',
      class: styles['icon-wrapper--1_25--none'],
    },
    {
      iconSize: 'lg',
      padding: 'fixedWidth',
      class: styles['icon-wrapper--1_25--fixedwidth'],
    },
    {
      iconSize: 'lg',
      padding: 'square',
      class: styles['icon-wrapper--1_25--square'],
    },
    {
      iconSize: 'lg',
      padding: 'roomy',
      class: styles['icon-wrapper--1_25--roomy'],
    },
    {
      iconSize: 'sm',
      padding: 'none',
      class: styles['icon-wrapper--1--none'],
    },
    {
      iconSize: 'sm',
      padding: 'fixedWidth',
      class: styles['icon-wrapper--1--fixedwidth'],
    },
    {
      iconSize: 'sm',
      padding: 'square',
      class: styles['icon-wrapper--1--square'],
    },
    {
      iconSize: 'sm',
      padding: 'roomy',
      class: styles['icon-wrapper--1--roomy'],
    },
    {
      iconSize: 'xs',
      padding: 'none',
      class: styles['icon-wrapper--1--none'],
    },
    {
      iconSize: 'xs',
      padding: 'fixedWidth',
      class: styles['icon-wrapper--1--fixedwidth'],
    },
    {
      iconSize: 'xs',
      padding: 'square',
      class: styles['icon-wrapper--1--square'],
    },
    {
      iconSize: 'xs',
      padding: 'roomy',
      class: styles['icon-wrapper--1--roomy'],
    },
    {
      iconSize: '2xs',
      padding: 'none',
      class: styles['icon-wrapper--0_75--none'],
    },
    {
      iconSize: '2xs',
      padding: 'fixedWidth',
      class: styles['icon-wrapper--0_75--fixedwidth'],
    },
    {
      iconSize: '2xs',
      padding: 'square',
      class: styles['icon-wrapper--0_75--square'],
    },
    {
      iconSize: '2xs',
      padding: 'roomy',
      class: styles['icon-wrapper--0_75--roomy'],
    },
    {
      iconSize: 'xl',
      padding: 'none',
      class: styles['icon-wrapper--2--none'],
    },
    {
      iconSize: 'xl',
      padding: 'fixedWidth',
      class: styles['icon-wrapper--2--fixedwidth'],
    },
    {
      iconSize: 'xl',
      padding: 'square',
      class: styles['icon-wrapper--2--square'],
    },
    {
      iconSize: 'xl',
      padding: 'roomy',
      class: styles['icon-wrapper--2--roomy'],
    },
    {
      iconSize: '2xl',
      padding: 'none',
      class: styles['icon-wrapper--3--none'],
    },
    {
      iconSize: '2xl',
      padding: 'fixedWidth',
      class: styles['icon-wrapper--3--fixedwidth'],
    },
    {
      iconSize: '2xl',
      padding: 'square',
      class: styles['icon-wrapper--3--square'],
    },
    {
      iconSize: '2xl',
      padding: 'roomy',
      class: styles['icon-wrapper--3--roomy'],
    },
  ],
});

const icon = cva(styles.icon, {
  variants: {
    padding: {
      none: '',
      fixedWidth: styles.fixedWidth,
      square: styles.square,
      roomy: styles.roomy,
    },
    iconSize: {
      '0.5x': styles.iconSize0_5x,
      '0.75x': styles.iconSize0_75x,
      '1x': styles.iconSize1x,
      '1.25x': styles.iconSize1_25x,
      '1.5x': styles.iconSize1_5x,
      '2x': styles.iconSize2x,
      '2.5x': styles.iconSize2_5x,
      '3x': styles.iconSize3x,
      '4x': styles.iconSize4x,
      '5x': styles.iconSize5x,
      '6x': styles.iconSize6x,
      '7x': styles.iconSize7x,
      '8x': styles.iconSize8x,
      '9x': styles.iconSize9x,
      '10x': styles.iconSize10x,
      default: styles.iconSize1x,
      lg: styles.iconSize1_25x,
      sm: styles.iconSize1x,
      xs: styles.iconSize1x,
      '2xs': styles.iconSize0_75x,
      xl: styles.iconSize2x,
      '2xl': styles.iconSize3x,
    },
  },
});

export type IconSize =
  | '0.5x'
  | '0.75x'
  | '1x'
  | '1.25x'
  | '1.5x'
  | '2x'
  | '2.5x'
  | '3x'
  | '4x'
  | '5x'
  | '6x'
  | '7x'
  | '8x'
  | '9x'
  | '10x'
  | 'default'
  | 'lg'
  | 'sm'
  | 'xs'
  | '2xs'
  | 'xl'
  | '2xl';

type ResponsivePropKeys = 'iconSize';

export interface IconBaseProps
  extends Omit<
    React.HTMLAttributes<HTMLElement>,
    'className' | 'style' | 'color'
  > {
  /**
   * What Icon to load from fontAwesome
   */
  iconName: string;

  /**
   * The prefix of the icon to load.
   * @default 'fas'
   */
  iconPrefix?: 'fas' | 'far' | 'fal' | 'fab' | 'fad' | 'fak' | 'fat' | 'fakd';

  /**
   * The icon pack to load.
   * Example: 'brands'
   */
  iconPack?: string;

  /**
   * **[Responsive]** The size of the icon.
   */
  iconSize?: IconSize;

  /**
   * The color of the icon.
   */
  color?: ForegroundVariants['foregroundColor'];

  /**
   * The padding of the icon.
   * @default 'none'
   */
  padding?: 'none' | 'fixedWidth' | 'square' | 'roomy';

  /**
   * Flip the icon horizontally, vertically, or both.
   */
  flip?: 'horizontal' | 'vertical' | 'both';

  /**
   * Whether the icon should be displayed with a fixed width.
   */
  fixedWidth?: boolean;

  /**
   * Whether the icon should be used as a list item.
   */
  listItem?: boolean;

  /**
   * Whether the icon has a border
   */
  border?: boolean;

  /**
   * Rotate the icon.
   */
  rotation?: 0 | 90 | 180 | 270;

  /**
   * Pull the icon to the left or right. Wrap text around an icon with this utility.
   */
  pull?: 'left' | 'right';

  /**
   * className to be passed to the icon
   */
  className?: string;

  /**
   * Whether the icon is purely decorative and should be hidden from screen readers.
   * @default false
   * @deprecated Use `aria-label` instead.
   */
  isDecorative?: boolean;

  /**
   * Whether the icon should spin.
   */
  spin?: boolean;

  /**
   * Background color class for the icon used solely in iconBackdrop component
   * @ignore
   */
  privateBgColorVariant?: BackgroundVariants['backgroundColor'];
}

// Defined inline (rather than via WithResponsiveProps) so react-docgen-typescript can resolve every prop for Storybook controls
export type IconProps = Omit<IconBaseProps, ResponsivePropKeys> & {
  iconSize?: ResponsiveValue<IconSize>;
} & Record<string, unknown>;

export const Icon = ({
  iconName,
  iconPrefix = 'far',
  iconSize = '1x',
  iconPack,
  color,
  padding = 'none',
  flip,
  fixedWidth = true,
  listItem,
  border,
  rotation,
  pull,
  isDecorative,

  spin,
  className,
  privateBgColorVariant,
  ...props
}: IconProps) => {
  if (isDecorative !== undefined) {
    altoConsole.warn(
      '`isDecorative` prop is deprecated and will be removed in future versions due to FontAwesome v7 accessibility changes. Please use `aria-label` instead.',
      { scope: 'Icon' },
    );
  }

  // Internal state to manage a unique key for re-renders
  const [key, setKey] = useState(0);
  useEffect(() => {
    // Update key whenever props that affect rendering change
    setKey((prevKey) => prevKey + 1);
  }, [
    iconName,
    iconPrefix,
    iconSize,
    iconPack,
    color,
    padding,
    flip,
    fixedWidth,
    listItem,
    border,
    rotation,
    pull,
    spin,
    privateBgColorVariant,
  ]);

  const baseIconSize = getBaseValue(iconSize, '1x');
  const iconSizeResponsiveClasses = getResponsiveClasses(iconSize, 'icon-size');

  // Use fontawesome size classes
  const fontAwesomeSize = [
    '2x',
    '3x',
    '4x',
    '5x',
    '6x',
    '7x',
    '8x',
    '9x',
    '10x',
  ].includes(baseIconSize)
    ? `fa-${baseIconSize}`
    : '';

  // Construct the class names
  const classNames = [
    iconPrefix,
    `fa-${iconName}`,
    fontAwesomeSize,
    iconPack ? `fa-${iconPack}` : '',
    flip ? `fa-flip-${flip}` : '',
    fixedWidth ? 'fa-fw' : '',
    listItem ? 'fa-li' : '',
    border ? 'fa-border' : '',
    rotation ? `fa-rotate-${rotation}` : '',
    pull ? `fa-pull-${pull}` : '',
    spin ? 'fa-spin' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const { 'data-testid': rawTestId, ...iconProps } = props;
  const testId = typeof rawTestId === 'string' ? rawTestId : undefined;

  return (
    <span
      key={key}
      className={[
        'icon-wrapper',
        iconWrapper({ iconSize: baseIconSize, padding }),
        ...iconSizeResponsiveClasses,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid={testId ? `${testId}-wrapper` : undefined}
    >
      <i
        className={[
          classNames,
          icon({ padding, iconSize: baseIconSize }),
          ...iconSizeResponsiveClasses,
          foregroundColorVariants({ foregroundColor: color }),
          backgroundColorVariants({
            backgroundColor: privateBgColorVariant ?? 'transparent',
          }),
        ]
          .filter(Boolean)
          .join(' ')}
        data-testid={testId}
        {...iconProps}
      />
    </span>
  );
};

export default Icon;
